import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as FormActions from '../../actions/actionCreators';
import Step from './Step';
import Form from './Form';
import DropdownField from './DropdownField';
import TextField from './TextFieldWrapper';
import RadioField from './RadioField';
import CheckboxField from './CheckboxField';
import BankField from './BankField';
import UploadField from './UploadField';
import Modal from './Modal';
import FormProgressStep from './FormProgressStep';
import ConditionalCheckboxGroupField from './ConditionalCheckboxGroupField';
import '../Loader/loader.less';

import {
  Regions,
  BankRelationShip,
  PrivacyOption,
  ContactPreference,
  ContactPreferenceNotAgree,
  DemographicAges,
  PronounOptions,
  DemographicHearAboutUs,
  DemographicGender,
  DemographicEthnicities,
} from '../../constants/FormContent';
import { bindActionCreators } from '@reduxjs/toolkit';
import { DISAGREE_TO_SHARE_INFO } from '../../constants/ErrorMessages';

interface FormContainerProps {
  formComplete: boolean;
  showComplaintField: boolean;
  formActions: any;
  representativeDetail: any;
  currentIndex: number;
  formType: 'contact' | 'report' | 'complaint';
  action: string;
  contactDetails: any;
  yourComplaint: any;
  agreementDetails: any;
  formStatus: string;
  formIntro: any;
  demographicInformation: any;
  banks: any;
  personalDetails: any;
  showDemographicFindOutReferral: boolean;
  privacyAuthorisation: any;
  showPreview?: boolean;
}

const FormContainer: React.FC<FormContainerProps> = (props) => {
  const [submitting, setSubmitting] = useState(false);

  const {
    currentIndex,
    formType,
    action,
    contactDetails,
    yourComplaint,
    personalDetails,
    agreementDetails,
    formComplete,
    formActions,
    formStatus,
    formIntro,
    demographicInformation,
    banks,
    showDemographicFindOutReferral,
    showComplaintField,
    privacyAuthorisation,
    representativeDetail,
  } = props;

  const {
    personalPrivacy: { value },
    contactPreference,
  } = personalDetails || {
    personalPrivacy: {
      value: 'I agree',
    },
  };

  const { onBehalf } = representativeDetail || {};
  const isRepresentingOnBehalfOfSomeone =
    onBehalf && onBehalf.value === 'Someone else';

  const isAgreeToShareInfo = value !== DISAGREE_TO_SHARE_INFO;

  const bankFieldHelperTest = formType === 'report' ? null : 'Click to select:';

  const isPhoneRequired =
    contactPreference && contactPreference.value === 'Phone';

  const renderBankField = !banks.length ? (
    <div className="loader" />
  ) : (
    <BankField
      label="Which bank is your complaint about?"
      helperText={bankFieldHelperTest}
      name="bank"
      required
      form="bankDetails"
    />
  );

  const renderModal = () => {
    const { showPreview } = props;
    const body = document.body.style;

    if (!formComplete || !showPreview) {
      body.removeProperty('overflow');
      return null;
    }

    body.setProperty('overflow', 'hidden');

    return <Modal action={action} formType={formType} />;
  };

  const { region, pronouns } = contactDetails || {};
  const { demographicGender, demographicFindOutReferral } =
    demographicInformation || {};

  const { privacyAgreement } = agreementDetails || {};
  const regionOutsideNZ = region && region.value === 'Outside of New Zealand';

  // conditional fields
  const pronounsOther =
    pronouns && pronouns.value === 'Prefer to self-describe';
  const repPronounsOther =
    representativeDetail &&
    representativeDetail.repPronouns.value === 'Prefer to self-describe';
  const genderOther =
    demographicGender && demographicGender.value === 'Prefer to self-describe';
  const demographicFindOutReferralDetailPlaceholder =
    demographicFindOutReferral &&
    demographicFindOutReferral.value === 'Community or advisory service'
      ? 'Which service?'
      : 'Please specify';

  const isNotAgreeToShare =
    privacyAgreement && privacyAgreement.value === 'I do not agree';

  const doNotAgreePrivacy =
    privacyAuthorisation &&
    privacyAuthorisation.privacyAgreement.value === 'I do not agree';

  const bankValue = yourComplaint?.bank?.value || '';

  const typeLabel = formType === 'report' ? 'submission' : formType;
  const btnLabel = submitting ? 'Submitting..' : `Submit ${typeLabel}`;
  const gridClass =
    formType === 'complaint'
      ? 'grid--16 grid__lg--12 grid__xl--10 grid--center'
      : 'grid--16 grid__lg--10 grid__xl--10 grid--center';

  const handleSubmit = () => {
    setSubmitting(true);

    if (formType === 'report') {
      formActions.submitReportForm(action);
    } else {
      formActions.submitCompleteForm(action);
    }
  };

  useEffect(() => {
    if (formType === 'report') {
      formActions.getWhistleBlowerBanks();
    } else {
      formActions.getBanks();
    }
  }, [formType, formActions]);

  return (
    <div className={gridClass}>
      {formType === 'contact' ? (
        <div className="form form--complaint">
          <Step
            title="Contact us"
            name="contactUs"
            formType={formType}
            index={0}
          >
            <Form
              name="contactUs"
              index={0}
              formAction={action}
              formType={formType}
              lastStep
            >
              <TextField
                name="firstName"
                label="First name"
                form={formType}
                fullWidth
                required
              />
              <TextField
                name="lastName"
                label="Last name"
                fullWidth
                form={formType}
                required
              />
              <TextField
                type="email"
                name="emailAddress"
                label="Email"
                fullWidth
                required
                form={formType}
              />
              <DropdownField
                name="region"
                options={Regions}
                label="Region"
                form={formType}
                fullWidth
                required
              />
            </Form>
          </Step>
        </div>
      ) : formType === 'report' ? (
        <div className="form form--complaint">
          <Step
            title="1. The bank involved"
            name="bankDetails"
            index={0}
            formType={formType}
          >
            <Form
              name="bankDetails"
              index={0}
              formAction={action}
              formType={formType}
            >
              {renderBankField}
            </Form>
          </Step>
          <Step
            title="2. Your relationship to this bank"
            name="relationDetails"
            index={1}
            formType={formType}
          >
            <Form
              name="relationDetails"
              index={1}
              formAction={action}
              formType={formType}
            >
              <RadioField
                name="bankRelationship"
                options={BankRelationShip}
                alignEnd
                form="relationDetails"
                required
              />
              <p className="mt--2">
                If you are a customer of a New Zealand bank and wish to make a
                complaint, see the Banking Ombudsman's{' '}
                <a href="https://bankomb.org.nz/make-a-complaint/complaint-form/">
                  Customer Complaint Form
                </a>
                .
              </p>
            </Form>
          </Step>

          <Step
            title="3. Your concerns"
            name="concernDetails"
            index={2}
            formType={formType}
          >
            <Form
              name="concernDetails"
              index={2}
              formAction={action}
              formType={formType}
            >
              <TextField
                name="concern"
                className="mt--3 mdc-text-field--extrapadding-two-lines"
                label=""
                textarea
                // eslint-disable-next-line max-len
                placeholder="Please include as much detail as possible, including dates, events and people you believe are involved in the wrongdoing. If you have previously raised your concerns with the bank, tell us what happened."
                rows="8"
                required
                fullWidth
                form="concernDetails"
              />
            </Form>
          </Step>

          <Step
            title="4. Relevant documents (optional)"
            name="documentsDetails"
            index={3}
            formType={formType}
          >
            <Form name="documentsDetails" index={3} formType={formType}>
              <UploadField
                type="file"
                name="documents"
                label="Choose files to upload"
                // eslint-disable-next-line max-len
                instruction="Please attach a maximum of five files about your concerns (each attachment cannot exceed 5MB)."
                hideOptional
                form="documentsDetails"
              />
            </Form>
          </Step>

          <Step
            title="5. Your expectations"
            name="expectationDetails"
            index={4}
            formType={formType}
          >
            <Form
              name="expectationDetails"
              index={4}
              formAction={action}
              formType={formType}
            >
              <TextField
                name="expectation"
                className="mt--3 mdc-text-field--extrapadding-two-lines"
                label="What would you like to happen as a result of raising your concerns?"
                textarea
                form="expectationDetails"
                rows={8}
                required
                fullWidth
              />
            </Form>
          </Step>

          <Step
            title="6. Your personal information"
            name="personalDetails"
            index={5}
            formType={formType}
          >
            <Form
              name="personalDetails"
              index={5}
              formAction={action}
              formType={formType}
            >
              <p className="mt--0">
                Providing your personal information (name and contact details)
                is voluntary.
              </p>
              <RadioField
                name="personalPrivacy"
                options={PrivacyOption}
                alignEnd
                form="personalDetails"
                required
              />
              {isAgreeToShareInfo ? (
                <h3 className="mt--3">Your contact details (optional)</h3>
              ) : (
                <p className="mt--3">
                  We offer a{' '}
                  <a
                    href="https://bankwb.org.nz/assets/Anonymous-contact-link.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    contact link
                  </a>{' '}
                  which you can use to communicate with us anonymously. If you
                  choose to enter your email address, our staff do not receive
                  or have access to it. It will be stored in a secure, encrypted
                  database solely to allow the link to be sent to you.
                </p>
              )}
              <TextField
                name="firstName"
                fullWidth
                hidden={!isAgreeToShareInfo}
                form="personalDetails"
              />
              <TextField
                name="lastName"
                fullWidth
                hidden={!isAgreeToShareInfo}
                form="personalDetails"
              />
              <TextField
                name="emailAddress"
                fullWidth
                required={isAgreeToShareInfo}
                form="personalDetails"
              />
              <TextField
                type="tel"
                name="phoneNumber"
                required={isPhoneRequired}
                hidden={!isAgreeToShareInfo}
                maxLength={13}
                minLength={8}
                form="personalDetails"
              />
              <DropdownField
                name="region"
                options={Regions}
                fullWidth
                hidden={!isAgreeToShareInfo}
                form="personalDetails"
              />
              <RadioField
                name="contactPreference"
                label="Please select your preferred contact method:"
                options={
                  isAgreeToShareInfo
                    ? ContactPreference
                    : ContactPreferenceNotAgree
                }
                form="personalDetails"
              />
              <TextField
                name="preferences"
                className="mt--3 mdc-text-field--extrapadding-two-lines"
                textarea
                rows="8"
                fullWidth
                form="personalDetails"
                preferences
              />
            </Form>
          </Step>

          <Step
            title="7. Agreement to share your concerns"
            name="agreementDetails"
            index={6}
            formType={formType}
          >
            <Form
              name="agreementDetails"
              index={6}
              formAction={action}
              formType={formType}
            >
              <h3 className="step__heading mt--0">
                By sharing your concerns with us you agree that:
              </h3>
              <ul className="form__list">
                <li>
                  We will prepare and provide a report, based on the information
                  you share with us, to the bank involved.
                </li>
                <li>
                  We may ask for additional information from you if we consider
                  it is relevant to your concerns. However, it is your choice
                  whether you provide further information.
                </li>
                <li>
                  Information about any review or investigation the bank may
                  carry out, including the general outcome, may be shared with
                  us.
                </li>
              </ul>
              <RadioField
                name="privacyAgreement"
                options={[
                  {
                    label: 'I agree',
                    value: 'I agree',
                  },
                  {
                    label: 'I do not agree',
                    value: 'I do not agree',
                  },
                ]}
                alignEnd
                form="agreementDetails"
                required
              />

              {isNotAgreeToShare && (
                <p className="mt-1">
                  If you do not want us to share the Report with the bank, we
                  will only be able to provide you with general information
                  about alternative ways you may raise your concern.
                </p>
              )}
            </Form>
          </Step>

          <Step
            title="8. Declarations"
            name="declarationDetails"
            index={7}
            formType={formType}
          >
            <Form
              name="declarationDetails"
              index={7}
              lastStep
              formType={formType}
            >
              <p className="step__heading mt--0 mb--0">I confirm that:</p>
              <CheckboxField name="confirm" required label="" form="declarationDetails" />
              <p className="step__heading mt--0 mb--0">I understand that:</p>
              <CheckboxField name="understand1" required label="" form="declarationDetails" />
            </Form>
          </Step>
          {renderModal()}
        </div>
      ) : (
        <div className="form form--complaint form--stepper">
          {!submitting ? (
            <FormProgressStep
              currentStep={currentIndex}
              formType={formType}
              index={0}
              formIntro={formIntro}
              showFormIntro
              title=""
            >
              <Form
                name="representativeDetails"
                index={0}
                formAction={action}
                formType={formType}
              >
                <RadioField
                  name="onBehalf"
                  options={[
                    {
                      label: 'Myself',
                      value: 'Myself',
                    },
                    {
                      label: 'Someone else',
                      value: 'Someone else',
                    },
                  ]}
                  alignEnd
                  label="Are you making this complaint on behalf of someone else?"
                  form="representativeDetails"
                />
                <h3 hidden={!isRepresentingOnBehalfOfSomeone}>
                  Representative details
                </h3>
                <DropdownField
                  name="repPersonalTitle"
                  label="Your title"
                  options={[
                    {
                      label: 'Mr',
                      value: 'Mr',
                    },
                    {
                      label: 'Ms',
                      value: 'Ms',
                    },
                    {
                      label: 'Mrs',
                      value: 'Mrs',
                    },
                    {
                      label: 'Miss',
                      value: 'Miss',
                    },
                    {
                      label: 'Mx',
                      value: 'Mx',
                    },
                    {
                      label: 'Dr',
                      value: 'Dr',
                    },
                    {
                      label: 'Est of',
                      value: 'Est of',
                    },
                  ]}
                  hidden={!isRepresentingOnBehalfOfSomeone}
                  required={isRepresentingOnBehalfOfSomeone}
                  form="representativeDetails"
                />
                <DropdownField
                  name="repPronouns"
                  hidden={!isRepresentingOnBehalfOfSomeone}
                  options={PronounOptions}
                  form="representativeDetails"
                />
                {repPronounsOther && (
                  <TextField
                    hidden={!isRepresentingOnBehalfOfSomeone}
                    name="repPronounsOther"
                    fullWidth
                    form="representativeDetails"
                    required
                  />
                )}
                <TextField
                  name="repFirstName"
                  label="First name"
                  hidden={!isRepresentingOnBehalfOfSomeone}
                  required={isRepresentingOnBehalfOfSomeone}
                  form="representativeDetails"
                />
                <TextField
                  name="repLastName"
                  label="Last name"
                  hidden={!isRepresentingOnBehalfOfSomeone}
                  required={isRepresentingOnBehalfOfSomeone}
                  form="representativeDetails"
                />
                <TextField
                  type={isRepresentingOnBehalfOfSomeone ? 'email' : 'text'}
                  name="repEmail"
                  label="Email"
                  form="representativeDetails"
                  hidden={!isRepresentingOnBehalfOfSomeone}
                  required={isRepresentingOnBehalfOfSomeone}
                />
                <TextField
                  type="tel"
                  name="repPhone"
                  label="Phone number"
                  hidden={!isRepresentingOnBehalfOfSomeone}
                  required={isRepresentingOnBehalfOfSomeone}
                  maxLength={13}
                  minLength={8}
                  form="representativeDetails"
                />
                <TextField
                  name="repOrganisation"
                  label="Organisation"
                  hidden={!isRepresentingOnBehalfOfSomeone}
                  form="representativeDetails"
                />
                <TextField
                  className="form__fieldholder--full-width"
                  name="repRelationship"
                  label="Your relationship to the complainant"
                  form="representativeDetails"
                  fullWidth
                  hidden={!isRepresentingOnBehalfOfSomeone}
                  required={isRepresentingOnBehalfOfSomeone}
                />
                {isRepresentingOnBehalfOfSomeone && (
                  <TextField
                    className="mdc-text-field--extrapadding-two-lines"
                    name="preferences"
                    form="representativeDetails"
                    label=""
                    textarea
                    rows="8"
                    fullWidth
                    preferences
                    column
                    hidden={!isRepresentingOnBehalfOfSomeone}
                  />
                )}
              </Form>
              <Form
                name="contactDetails"
                index={1}
                formAction={action}
                formType={formType}
              >
                <DropdownField
                  name="title"
                  form="contactDetails"
                  label="Your title"
                  options={[
                    {
                      label: 'Mr',
                      value: 'Mr',
                    },
                    {
                      label: 'Ms',
                      value: 'Ms',
                    },
                    {
                      label: 'Mrs',
                      value: 'Mrs',
                    },
                    {
                      label: 'Miss',
                      value: 'Miss',
                    },
                    {
                      label: 'Mx',
                      value: 'Mx',
                    },
                    {
                      label: 'Dr',
                      value: 'Dr',
                    },
                    {
                      label: 'Est of',
                      value: 'Est of',
                    },
                  ]}
                  required
                />
                <DropdownField
                  name="pronouns"
                  options={PronounOptions}
                  form="contactDetails"
                />
                {pronounsOther && (
                  <TextField
                    name="pronounsOther"
                    form="contactDetails"
                    fullWidth
                    required
                  />
                )}
                <TextField
                  name="firstName"
                  label="First name"
                  required
                  form="contactDetails"
                />
                <TextField
                  name="lastName"
                  label="Last name"
                  required
                  form="contactDetails"
                />
                <TextField
                  type="email"
                  name="emailAddress"
                  label="Email"
                  required
                  form="contactDetails"
                />
                <TextField
                  type="tel"
                  name="phoneNumber"
                  label="Phone number"
                  required
                  form="contactDetails"
                  maxLength={13}
                  minLength={8}
                />
                <TextField
                  name="organisation"
                  label="Organisation"
                  form="contactDetails"
                />
                <DropdownField
                  className="mdc-text-field--upgraded"
                  name="region"
                  label="Region"
                  form="contactDetails"
                  options={Regions}
                  required
                />
                {regionOutsideNZ && (
                  <p className="form__fieldholder form__fieldholder--fullWidth form__fieldholder--smallMargin">
                    We are only able to assist with complaints about New Zealand
                    banks
                  </p>
                )}
                {!isRepresentingOnBehalfOfSomeone && (
                  <TextField
                    className="mdc-text-field--extrapadding-two-lines"
                    name="preferences"
                    label=""
                    textarea
                    rows="6"
                    fullWidth
                    preferences
                    column
                    form="contactDetails"
                    hidden={isRepresentingOnBehalfOfSomeone}
                  />
                )}
              </Form>

              <Form
                name="yourComplaint"
                index={2}
                formAction={action}
                formType={formType}
              >
                {renderBankField}

                {showComplaintField && (
                  <TextField
                    name="complaint"
                    label="What is your complaint about?"
                    className="mt--3"
                    textarea
                    // eslint-disable-next-line max-len
                    placeholder="Include details about when and where events occurred, and whether you have raised this concern with the bank already."
                    rows="8"
                    fullWidth
                    form="yourComplaint"
                    required
                    column
                  />
                )}
              </Form>

              <Form
                index={3}
                formAction={action}
                formType={formType}
                name="yourExpectations"
              >
                <TextField
                  name="expectation"
                  className="mdc-text-field--extrapadding"
                  heading="What would you like to happen?"
                  textarea
                  form="yourExpectations"
                  rows="8"
                  required
                  fullWidth
                  column
                />
              </Form>

              <Form
                name="relevantDocuments"
                index={4}
                formAction={action}
                formType={formType}
              >
                <UploadField
                  type="file"
                  name="documents"
                  label="Choose files to upload"
                  title="Relevant documents"
                  form=""
                />
              </Form>
              <Form
                name="privacyAuthorisation"
                index={5}
                formType={formType}
                formAction={action}
              >
                {!isRepresentingOnBehalfOfSomeone ? (
                  <div>
                    <h3 className="step__heading mt--0">Privacy agreement</h3>
                    <p>
                      We need your permission to discuss your complaint with the
                      bank so we can help resolve it. Please confirm we (the
                      Banking Ombudsman Scheme) can:{' '}
                    </p>
                    <ul className="form__list">
                      <li>
                        Share the information you provide with {bankValue}.
                      </li>
                      <li>
                        Receive information from {bankValue} which is relevant
                        to your complaint.
                      </li>
                    </ul>
                  </div>
                ) : (
                  <div>
                    <h3 className="step__heading mt--0">Privacy agreement</h3>
                    <p>
                      We need the customer's permission to discuss the complaint
                      with you and the bank so we can help resolve it. We will
                      contact you to arrange the appropriate authorisation.
                    </p>
                  </div>
                )}
                {!isRepresentingOnBehalfOfSomeone && (
                  <RadioField
                    name="privacyAgreement"
                    options={[
                      {
                        label: 'I agree',
                        value: 'I agree',
                      },
                      {
                        label: 'I do not agree',
                        value: 'I do not agree',
                      },
                    ]}
                    alignEnd
                    noLabel
                    form="privacyAuthorisation"
                    hidden={isRepresentingOnBehalfOfSomeone}
                    required={!isRepresentingOnBehalfOfSomeone}
                  />
                )}

                {doNotAgreePrivacy && (
                  <p className="mt-1">
                    This means we will be unable to discuss your complaint with{' '}
                    {bankValue}. This may limit our ability to assist you.
                  </p>
                )}
                <p>
                  <a
                    href="/about-us/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read our privacy policy
                  </a>{' '}
                  to see how we collect, store and manage information.
                </p>
              </Form>
              <Form
                name="demographicInformation"
                index={6}
                formType={formType}
                formAction={action}
              >
                <div className="mb--3">
                  <h3 className="step__heading mt--0">About you (optional)</h3>
                  <p>
                    To deliver better banking for Aotearoa, it's helpful for us
                    to know who uses our service. We want to be accessible to
                    everyone. These questions are optional but we really
                    appreciate your feedback.
                  </p>
                  <p>
                    Your answers will not be provided to the bank and will not
                    affect how we handle your complaint.
                  </p>
                  {isRepresentingOnBehalfOfSomeone && (
                    <p>
                      Please provide the customer&apos;s information rather than
                      your own.
                    </p>
                  )}
                </div>
                <DropdownField
                  name="demographicAge"
                  label="Age"
                  options={DemographicAges}
                  form="demographicInformation"
                />
                <DropdownField
                  name="demographicGender"
                  label="Gender"
                  options={DemographicGender}
                  form="demographicInformation"
                />
                {genderOther && (
                  <TextField
                    name="demographicGenderOther"
                    label="Self described gender"
                    fullWidth
                    form="demographicInformation"
                  />
                )}
                <ConditionalCheckboxGroupField
                  label="Ethnicity"
                  name="demographicEthnicity"
                  options={DemographicEthnicities}
                  form="demographicInformation"
                />
                <DropdownField
                  name="demographicFindOutReferral"
                  label="How did you find out about us?"
                  className="form__fieldholder--full-width"
                  options={DemographicHearAboutUs}
                  fullWidth
                  form="demographicInformation"
                />
                {showDemographicFindOutReferral && (
                  <TextField
                    placeholder={demographicFindOutReferralDetailPlaceholder}
                    className="form__fieldholder--full-width"
                    name="demographicFindOutReferralDetail"
                    fullWidth
                    form="demographicInformation"
                  />
                )}
              </Form>
              <Form
                name="submit"
                index={7}
                lastStep
                formType={formType}
                formAction={action}
              >
                <div className="form__bottom-section">
                  <div className="text-center">
                    <p>
                      You can click through the sections above if you wish to
                      review your answers.
                      <br />
                      Once you submit your complaint, you will receive a copy
                      via email.
                    </p>
                  </div>
                  <button
                    type="submit"
                    className={`form__submit-button step__button button button--quarternary ${
                      submitting ? 'button--loading' : ''
                    } `}
                    disabled={
                      !formComplete || formStatus === 'loading' || submitting
                    }
                    onClick={handleSubmit}
                  >
                    {submitting ? <div className="loader"></div> : null}

                    {btnLabel}
                  </button>
                </div>
              </Form>
            </FormProgressStep>
          ) : (
            <>
              <div className="submission-holder">
                <div className="loader"></div>
                <p>Submitting form...</p>
              </div>
            </>
          )}
          {renderModal()}
          {formComplete && formType !== 'complaint' && (
            <div className="form__bottom-section">
              {formType !== 'complaint' && (
                <button
                  type="button"
                  className="form__preview-button step__button button button--upload"
                  onClick={() => formActions.setShowPreview(true)}
                >
                  Preview {formType === 'report' ? 'submission' : formType}
                </button>
              )}
              <button
                type="submit"
                className={`form__submit-button step__button button button--quarternary ${
                  submitting ? 'button--loading' : ''
                } `}
                disabled={
                  !formComplete || formStatus === 'loading' || submitting
                }
                onClick={handleSubmit}
              >
                {submitting ? <div className="loader"></div> : null}

                {btnLabel}
              </button>
            </div>
          )}
        </div>
      )}
      {formType !== 'contact' && formType !== 'complaint' && (
        <div className="form__bottom-section">
          {!formComplete && (
            <p>
              Please complete all sections to submit your{' '}
              {formType === 'report' ? 'submission' : formType}.
            </p>
          )}
          {formComplete && (
            <button
              type="button"
              className="form__preview-button step__button button button--upload"
              onClick={() => formActions.setShowPreview(true)}
            >
              Preview {formType === 'report' ? 'submission' : formType}
            </button>
          )}
          <button
            type="submit"
            className={`form__submit-button step__button button button--quarternary ${
              submitting ? 'button--loading' : ''
            }`}
            disabled={!formComplete || formStatus === 'loading' || submitting}
            onClick={handleSubmit}
          >
            {submitting ? <div className="loader"></div> : null}

            {btnLabel}
          </button>
        </div>
      )}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    formComplete: state.forms.representativeDetails
      ? (state.forms.representativeDetails.isComplete &&
          state.forms.contactDetails.isComplete &&
          state.forms.yourComplaint.isComplete &&
          state.forms.yourExpectations.isComplete &&
          state.forms.relevantDocuments.isComplete &&
          state.forms.privacyAuthorisation.isComplete &&
          state.forms.demographicInformation.isComplete) ||
        (state.forms.bankDetails.isComplete &&
          state.forms.relationDetails.isComplete &&
          state.forms.concernDetails.isComplete &&
          state.forms.documentsDetails.isComplete &&
          state.forms.expectationDetails.isComplete &&
          state.forms.personalDetails.isComplete &&
          state.forms.agreementDetails.isComplete &&
          state.forms.declarationDetails.isComplete)
      : false,
    currentIndex: state.forms.currentIndex,
    showPreview: state.forms.showPreview,
    showComplaintField: state.forms.yourComplaint
      ? !!state.forms.yourComplaint.bank.value
      : false,
    banksStatus: state.banks.status,
    banks: state.banks.options,
    representativeDetail: state.forms.representativeDetails,
    contactDetails: state.forms.contactDetails,
    yourComplaint: state.forms.yourComplaint,
    formStatus: state.submit.formStatus,
    personalDetails: state.forms.personalDetails,
    agreementDetails: state.forms.agreementDetails,
    demographicInformation: state.forms.demographicInformation,
    privacyAuthorisation: state.forms.privacyAuthorisation,
    showDemographicFindOutReferral: state.forms.demographicInformation
      ? !!(
          state.forms.demographicInformation.demographicFindOutReferral
            .value === 'Community or advisory service' ||
          state.forms.demographicInformation.demographicFindOutReferral
            .value === 'Another way'
        )
      : false,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    formActions: bindActionCreators(FormActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
